/**
 * CEC Dashboard
 * Clinical Events Committee workstation for adverse event adjudication
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { cecApi, CecEvent, CecSummary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Users,
  ChevronRight,
  Filter,
} from 'lucide-react';

const CecDashboard: React.FC = () => {
  const { studyCode } = useAuth();
  const selectedStudy = studyCode || '';
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'adjudicated'>('all');

  // Fetch CEC summary
  const { data: summaryData } = useQuery({
    queryKey: ['cec-summary', selectedStudy],
    queryFn: () => cecApi.getSummary(selectedStudy || undefined),
  });

  // Fetch adverse events
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['cec-events', selectedStudy, statusFilter],
    queryFn: () => cecApi.listEvents({
      studyCode: selectedStudy || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
    }),
  });
  const summary: CecSummary = summaryData?.summary || {
    totalAEs: 0,
    adjudicatedAEs: 0,
    pendingAEs: 0,
    adjudicationsByType: {},
  };
  const events: CecEvent[] = eventsData?.events || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CEC Dashboard</h1>
          <p className="text-gray-500">Clinical Events Committee - Adverse Event Adjudication</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total AEs</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalAEs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{summary.pendingAEs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Adjudicated</p>
              <p className="text-2xl font-bold text-green-600">{summary.adjudicatedAEs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalAEs > 0
                  ? Math.round((summary.adjudicatedAEs / summary.totalAEs) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Adjudication by Type */}
      {Object.keys(summary.adjudicationsByType).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Adjudications by Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.adjudicationsByType).map(([code, info]) => (
              <div key={code} className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">{info.formName}</p>
                <p className="text-xl font-bold text-gray-900">{info.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Adverse Events</h2>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'adjudicated')}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="adjudicated">Adjudicated</option>
            </select>
          </div>
        </div>

        {eventsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-opci-navy-500"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No adverse events found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Form
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Reported
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.map((event) => (
                  <tr key={event._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">
                        {event.subject?.displayId || event.subject?.subjectId}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {event.site?.siteName} ({event.site?.siteId})
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {event.formTemplate?.formName}
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {new Date(event.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          event.adjudicationStatus === 'adjudicated'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {event.adjudicationStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/data?subject=${event.subject?._id}&section=cec`}
                        className="inline-flex items-center gap-1 text-opci-navy-500 hover:text-opci-navy-600"
                      >
                        {event.adjudicationStatus === 'pending' ? 'Adjudicate' : 'View'}
                        <ChevronRight size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CecDashboard;
